import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useRestClient} from "../../../../client/SpmRestClient";
import {NodeView} from "../../../../types/Node";
import {useAlerts} from "../../../../utils/UserAlerts";
import {Spinner, Stack} from "react-bootstrap";
import {BsCaretDownFill, BsCaretRightFill} from "react-icons/bs";
import {ObjectUtil} from "zavadil-ts-common";

export type NodeTreeProps = {
	node?: NodeView;
	nodePath: Array<number>;
	selectedNode: NodeView | null;
	selectedNodePath: Array<number>;
	onNodeSelected: (node: NodeView, path: Array<number>) => void;
	onNodeDoubleClicked: (node: NodeView, path: Array<number>) => void;
}

export default function NodeTree({
	node,
	nodePath,
	selectedNode,
	selectedNodePath,
	onNodeSelected,
	onNodeDoubleClicked,
}: NodeTreeProps) {
	const restClient = useRestClient();
	const userAlerts = useAlerts();
	const [children, setChildren] = useState<Array<NodeView> | null | undefined>();
	const isRoot = useMemo(() => node === undefined, [node]);
	const isOnSelectedPath = useMemo(() => node && selectedNodePath.includes(Number(node.id)), [node, selectedNodePath]);
	const [collapsed, setCollapsed] = useState<boolean>(isOnSelectedPath || (!isRoot && ObjectUtil.notEmpty(node?.parentId)));

	const ref = useRef<HTMLDivElement>(null);
	const scrollHere = () => {
		if (ref.current) {
			ref.current.scrollIntoView(
				{
					behavior: 'smooth',
					block: 'center',
					inline: 'nearest',
				}
			);
		}
	};

	const isSelected = useMemo(() => selectedNode?.id === node?.id, [selectedNode, node]);

	useEffect(() => {
		if (isSelected) {
			scrollHere();
			setCollapsed(false);
		}
	}, [isSelected]);

	useEffect(() => {
		if (isOnSelectedPath) {
			setCollapsed(false);
		}
	}, [isOnSelectedPath]);

	useEffect(() => {
		if (children && selectedNode) {
			const replaceable = children.find((ch) => ch.id === selectedNode.id && ch !== selectedNode);
			if (replaceable) {
				setChildren(children.map((ch) => ch === replaceable ? selectedNode : ch));
			}
		}
	}, [selectedNode, children]);

	const loadChildren = useCallback(
		() => {
			if (collapsed || children !== undefined) return;
			setChildren(null);
			if (node?.id) {
				restClient.nodes
					.children(node.id)
					.then(setChildren)
					.catch((e) => userAlerts.err(e));
			} else {
				restClient.nodes
					.roots()
					.then(setChildren)
					.catch((e) => userAlerts.err(e));
			}
		},
		[restClient, node, userAlerts, collapsed, children]
	);

	useEffect(loadChildren, [node, collapsed]);

	const text = useMemo(
		() => {
			if (!node?.text) return '';
			const lines = node.text.split('\n');
			if (lines.length > 0) return lines[0];
			return '';
		},
		[node]
	);

	return (
		<div className={`nt-container cursor-default ${isRoot ? 'nt-root' : ''}`}>
			{
				node && <Stack direction="horizontal" className="align-items-start">
					<div onClick={() => setCollapsed(!collapsed)} className="collapsed-toggle">
						{
							collapsed ? <BsCaretRightFill/> : <BsCaretDownFill/>
						}
					</div>
					<div
						ref={ref}
						className={`nt-body px-2 ${isSelected ? 'nt-selected' : ''}`}
						onClick={() => onNodeSelected(node, nodePath)}
						onDoubleClick={() => onNodeDoubleClicked(node, nodePath)}
					>
						<div>
							<span className="font-weight-light">[{node.id}]</span>{' '}
							<strong>{node.answer}</strong>
						</div>
						{
							text.length > 0 && !collapsed && <div>{text}</div>
						}
					</div>
				</Stack>
			}
			<div className="nt-children">
				{
					children && !collapsed
						? children.map(child => {
							return (
								<NodeTree
									key={child.id}
									node={child}
									nodePath={[...nodePath, Number(child.id)]}
									selectedNode={selectedNode}
									selectedNodePath={selectedNodePath}
									onNodeSelected={onNodeSelected}
									onNodeDoubleClicked={onNodeDoubleClicked}
								/>
							);
						})
						: children === null && <Spinner/>
				}
			</div>
		</div>
	);
}
